// Playback engine for Jianpu Studio — schedules a parsed song's events onto
// the Home Key synth (lib/homeKeyAudio) with a metronome and a moving
// highlight. Timing uses per-event timeouts with a short audio lookahead, so
// stop() cuts things off within ~a tenth of a second.

import { useCallback, useEffect, useRef, useState } from "react";
import { getCtx, resumeAudio, playNote, PATCHES } from "../../lib/homeKeyAudio";

const LOOKAHEAD_MS = 120;

export const MELODY_PATCH = PATCHES.softKeys;

// A short metronome blip — accented (higher, louder) on the downbeat.
export function playClick(accent = false, whenMs = 0) {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + whenMs / 1000;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "square";
  osc.frequency.value = accent ? 1568 : 1046;
  gain.gain.setValueAtTime(accent ? 0.12 : 0.07, t);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t);
  osc.stop(t + 0.06);
}

// Free-running metronome (used while recording in the composer). Returns a
// { stop } handle. onBeat(beatNumber) fires at each click, beat 0 first.
export function startMetronome({ bpm, beatsPerBar = 4, onBeat }) {
  resumeAudio();
  const beatMs = 60000 / bpm;
  const t0 = performance.now() + 200;
  let beat = 0;
  let timer = null;
  let stopped = false;
  const scheduleNext = () => {
    if (stopped) return;
    const due = t0 + beat * beatMs;
    timer = setTimeout(() => {
      if (stopped) return;
      playClick(beat % beatsPerBar === 0, Math.max(0, due + LOOKAHEAD_MS - performance.now()));
      if (onBeat) onBeat(beat);
      beat += 1;
      scheduleNext();
    }, Math.max(0, due - performance.now() - LOOKAHEAD_MS));
  };
  scheduleNext();
  return {
    startAtMs: t0,
    stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
    },
  };
}

// useJianpuPlayer — drive one song at a time.
//
//   const player = useJianpuPlayer();
//   player.start({ events, bpm, beatsPerBar, totalBeats, metronome, guide,
//                  countInBeats, onDone });
//   player.stop();
//   player.playing, player.activeIndex     — for UI state
//   player.getSongTimeMs()                 — song-clock ms (negative during
//                                            the count-in) for scoring
export default function useJianpuPlayer() {
  const [playing, setPlaying] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const timersRef = useRef([]);
  const startedAtRef = useRef(0);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const stop = useCallback(() => {
    clearTimers();
    setPlaying(false);
    setActiveIndex(-1);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(
    ({
      events,
      bpm,
      beatsPerBar = 4,
      totalBeats,
      metronome = false,
      guide = true,
      countInBeats = 0,
      onEvent,
      onDone,
    }) => {
      clearTimers();
      resumeAudio();
      const beatMs = 60000 / bpm;
      const countInMs = countInBeats * beatMs;
      const now = performance.now();
      const t0 = now + 250 + countInMs; // song time zero
      startedAtRef.current = t0;
      setPlaying(true);
      setActiveIndex(-1);

      const at = (dueMs, fn, lead = 0) => {
        timersRef.current.push(setTimeout(fn, Math.max(0, dueMs - performance.now() - lead)));
      };

      // Count-in clicks.
      for (let b = 0; b < countInBeats; b++) {
        const due = t0 - countInMs + b * beatMs;
        at(due, () => playClick(b === 0, LOOKAHEAD_MS), LOOKAHEAD_MS);
      }

      // Metronome across the song.
      if (metronome) {
        for (let b = 0; b < Math.ceil(totalBeats); b++) {
          const due = t0 + b * beatMs;
          at(due, () => playClick(b % beatsPerBar === 0, LOOKAHEAD_MS), LOOKAHEAD_MS);
        }
      }

      // Notes: audio slightly ahead (scheduled into the future), highlight on time.
      for (const ev of events) {
        const due = t0 + ev.startMs;
        if (ev.kind === "note" && guide) {
          at(
            due,
            () => playNote(MELODY_PATCH, ev.midi, Math.max(120, ev.durMs * 0.92), LOOKAHEAD_MS),
            LOOKAHEAD_MS
          );
        }
        at(due, () => {
          setActiveIndex(ev.index);
          if (onEvent) onEvent(ev);
        });
      }

      const last = events[events.length - 1];
      const endMs = last ? last.startMs + last.durMs : 0;
      at(t0 + endMs + 350, () => {
        setPlaying(false);
        setActiveIndex(-1);
        if (onDone) onDone();
      });
    },
    []
  );

  const getSongTimeMs = useCallback(() => performance.now() - startedAtRef.current, []);

  return { playing, activeIndex, start, stop, getSongTimeMs };
}
